import htmx from "htmx.org";
import type { HtmxSettleInfo, HtmxSwapStyle } from "htmx.org";

interface HtmxInternalApi {
  oobSwap(swapStyle: string, node: Element, settleInfo: HtmxSettleInfo): void;
}

interface SwapEntry {
  existingNode: HTMLElement;
  newNode: HTMLElement;
}

let api: HtmxInternalApi | null = null;

function isJsonScriptElement(el: Element): el is HTMLScriptElement {
  return (
    el.tagName === "SCRIPT" &&
    el.getAttribute("type") === "application/json" &&
    el.hasAttribute("data-trigger-name")
  );
}

htmx.defineExtension("patch", {
  init(apiRef: HtmxInternalApi) {
    api = apiRef;
  },
  handleSwap(swapStyle: HtmxSwapStyle, _target: Node, fragment: Node, settleInfo: HtmxSettleInfo) {
    if (swapStyle !== "patch") return false;

    const swaps: SwapEntry[] = [];
    const children = Array.from((fragment as DocumentFragment).children) as HTMLElement[];

    for (const newNode of children) {
      if (isJsonScriptElement(newNode)) {
        const name = newNode.getAttribute("data-trigger-name")!;
        const detail: unknown = JSON.parse(newNode.textContent!);
        window.setTimeout(() => {
          document.dispatchEvent(new CustomEvent(name, { detail }));
        });
        continue;
      }

      if (!newNode.id) {
        fragment.removeChild(newNode);
        console.error("HTMX patch-swap: cannot patch element without ID.");
        continue;
      }

      const existingNode = document.getElementById(newNode.id);
      if (!existingNode) {
        fragment.removeChild(newNode);
        console.warn(
          "HTMX patch-swap: element with ID '" +
          newNode.id +
          "' not found in document."
        );
        continue;
      }

      swaps.push({ existingNode, newNode });
    }

    const immediateSwaps: SwapEntry[] = [];
    const transitionedSwaps: (SwapEntry & { transitionName: string })[] = [];
    const canAnimate =
      !!document.startViewTransition &&
      document.visibilityState === "visible" &&
      document.hasFocus();

    for (const { existingNode, newNode } of swaps) {
      const transitionName = newNode.getAttribute("data-view-transition");
      const isIdentical = existingNode.innerHTML === newNode.innerHTML;
      if (transitionName && canAnimate && !isIdentical) {
        transitionedSwaps.push({ existingNode, newNode, transitionName });
      } else {
        immediateSwaps.push({ existingNode, newNode });
      }
    }

    for (const { newNode } of immediateSwaps) {
      api!.oobSwap("innerHTML", newNode, settleInfo);
    }

    if (transitionedSwaps.length > 0) {
      for (const { existingNode, transitionName } of transitionedSwaps) {
        existingNode.style.viewTransitionName = transitionName;
      }

      document.documentElement.classList.add("view-transitioning");

      const cleanupTransition = () => {
        for (const { existingNode } of transitionedSwaps) {
          existingNode.style.viewTransitionName = "";
        }
        document.documentElement.classList.remove("view-transitioning");
      };

      let transition: ViewTransition;
      try {
        transition = document.startViewTransition(() => {
          for (const { newNode } of transitionedSwaps) {
            api!.oobSwap("innerHTML", newNode, settleInfo);
          }
        });
      } catch (error) {
        cleanupTransition();
        console.error("HTMX patch-swap: view transition failed to start.", error);
        return true;
      }

      transition.ready.catch(() => {});

      transition.updateCallbackDone
        .then(() => {
          for (const { existingNode } of transitionedSwaps) {
            htmx.process(existingNode);
          }
        })
        .catch((error) => {
          console.error("HTMX patch-swap: view transition update failed.", error);
        });

      transition.finished
        .catch((error) => {
          console.error("HTMX patch-swap: view transition aborted.", error);
        })
        .finally(cleanupTransition);
    }

    return true;
  },
});
